/*
 * Ergovanta PDP gallery: thumbnail-strip PREV / NEXT step controls (2026-06-22)
 *
 * Goal: a clear pair of prev/next controls associated with the product-gallery
 * thumbnail strip that let a shopper step through the images fast, on BOTH
 * desktop and mobile. Unlike a pure "scroll the strip" affordance, these arrows
 * ADVANCE THE ACTIVE IMAGE: clicking next selects the next slide, prev selects
 * the previous one. Horizon's slideshow then auto-centers the now-active
 * thumbnail in the strip (Slideshow#select -> #centerSelectedThumbnail), so the
 * active thumbnail always stays in view; no manual strip-panning needed.
 *
 * Wiring (uses Horizon's existing slideshow API, never fights it):
 *   - For each PDP thumbnail controls host
 *     <slideshow-controls thumbnails pagination-position="center"> we find the
 *     owning <slideshow-component> via closest() and drive it with its public
 *     previous()/next() methods (the same API the thumbnail <button on:click=
 *     "/select/N"> already uses).
 *   - We keep enabled/disabled + visible state in sync by listening to the
 *     bubbling 'slideshow:select' event (assets/events.js, SlideshowSelectEvent)
 *     and reading slides.length / current / atStart / atEnd off the component.
 *   - Buttons reuse the .eg-thumb-arrow class so the navy pill styling already
 *     in eg-overrides.css applies. If a sibling affordance script already
 *     injected .eg-thumb-arrow buttons, we ADOPT them (add the image-stepping
 *     behavior to the existing buttons) instead of creating a duplicate pair,
 *     so there is always exactly one set of arrows.
 *
 * Self-guarding (safe to load globally from snippets/stylesheets.liquid):
 *   - No-ops on any page without a multi-slide thumbnail gallery.
 *   - Idempotent per host; re-inits on Shopify section/block re-render and on
 *     variant swaps that re-render the gallery.
 *
 * Reversible: remove the <script> include in snippets/stylesheets.liquid, this
 * asset, and the "image-step" CSS block in eg-overrides.css.
 */
(function () {
  'use strict';

  var ICON_PREV =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_NEXT =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // The slideshow's public API lives on the <slideshow-component> custom element.
  function getSlideshow(host) {
    return host.closest('slideshow-component');
  }

  // How many real (non-hidden) slides the gallery currently has.
  function slideCount(slideshow) {
    try {
      var s = slideshow.slides; // getter, filters hidden slides
      return s ? s.length : 0;
    } catch (e) {
      return 0;
    }
  }

  function makeArrow(dir) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'eg-thumb-arrow eg-thumb-arrow--' + dir;
    btn.setAttribute('data-eg-galnav', dir);
    btn.setAttribute(
      'aria-label',
      dir === 'prev' ? 'Previous image' : 'Next image'
    );
    btn.innerHTML = dir === 'prev' ? ICON_PREV : ICON_NEXT;
    return btn;
  }

  // Attach the image-stepping click behavior to a prev/next button exactly once.
  function bindStep(btn, dir, slideshow) {
    if (btn.__egGalNavBound) return;
    btn.__egGalNavBound = true;
    btn.setAttribute('data-eg-galnav', dir);
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (btn.getAttribute('aria-disabled') === 'true') return;
      if (dir === 'prev') slideshow.previous(e);
      else slideshow.next(e);
    });
  }

  // Reflect current position onto the buttons: hide when <=1 slide, and grey out
  // the end-stops on a non-infinite gallery so the control reads honestly.
  function sync(host, slideshow, prevBtn, nextBtn) {
    var count = slideCount(slideshow);
    var multi = count > 1;
    host.setAttribute('data-eg-galnav-active', multi ? 'true' : 'false');

    var infinite = false;
    try {
      infinite = slideshow.infinite;
    } catch (e) {}

    var current = 0;
    try {
      current = slideshow.current || 0;
    } catch (e) {}

    var atStart = !infinite && current <= 0;
    var atEnd = !infinite && current >= count - 1;

    setDisabled(prevBtn, multi && atStart);
    setDisabled(nextBtn, multi && atEnd);
  }

  function setDisabled(btn, disabled) {
    if (!btn) return;
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    btn.classList.toggle('eg-thumb-arrow--disabled', !!disabled);
  }

  function initHost(host) {
    // Only the bottom/center horizontal strip; skip the vertical left/right
    // thumbnail layouts (different scroll axis, controls already fit).
    if (host.getAttribute('pagination-position') !== 'center') return;
    if (!host.hasAttribute('thumbnails')) return;

    var slideshow = getSlideshow(host);
    if (!slideshow) return;

    if (host.__egGalNavInit) {
      // Already wired: just refresh state (e.g. after a variant swap).
      sync(host, slideshow, host.__egPrevBtn, host.__egNextBtn);
      return;
    }
    host.__egGalNavInit = true;

    // Adopt existing .eg-thumb-arrow buttons if a sibling script injected them,
    // otherwise create our own. Either way: one prev + one next.
    var prevBtn = host.querySelector('.eg-thumb-arrow--prev');
    var nextBtn = host.querySelector('.eg-thumb-arrow--next');
    if (!prevBtn) {
      prevBtn = makeArrow('prev');
      host.appendChild(prevBtn);
    }
    if (!nextBtn) {
      nextBtn = makeArrow('next');
      host.appendChild(nextBtn);
    }
    host.__egPrevBtn = prevBtn;
    host.__egNextBtn = nextBtn;

    bindStep(prevBtn, 'prev', slideshow);
    bindStep(nextBtn, 'next', slideshow);

    // Keep state synced as the active slide changes (clicks, swipes, variant
    // media changes all dispatch slideshow:select on the component).
    slideshow.addEventListener('slideshow:select', function () {
      sync(host, slideshow, prevBtn, nextBtn);
    });

    // Slides can be revealed/hidden by variant filtering; recheck on resize too.
    window.addEventListener('resize', function () {
      sync(host, slideshow, prevBtn, nextBtn);
    });

    // The slideshow sets up asynchronously (waits a frame for layout); recheck
    // shortly after init so the count/end-stops are correct on first paint.
    sync(host, slideshow, prevBtn, nextBtn);
    requestAnimationFrame(function () {
      sync(host, slideshow, prevBtn, nextBtn);
    });
    setTimeout(function () {
      sync(host, slideshow, prevBtn, nextBtn);
    }, 350);
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var hosts = root.querySelectorAll(
      "slideshow-controls[thumbnails][pagination-position='center']"
    );
    Array.prototype.forEach.call(hosts, initHost);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme-editor re-renders + Horizon variant swaps that re-render the gallery.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    initAll(e.target);
  });
})();
